import axios from 'axios';
import { useAuthStore } from '../stores/auth.store';

/** Base URL of the JobTetris app ("Return to JobTetris", prod re-auth). */
export const JT_APP_URL: string = import.meta.env.VITE_JT_APP_URL || 'https://app.jobtetris.com';

/**
 * 403 codes that mean the session itself is over, not just that one action
 * was refused:
 *  - ENGAGE_ACCESS_REVOKED     — Engage access was switched off in JT
 *  - MANAGE_AS_ACTOR_NOT_ADMIN — the admin behind a Manage-as session lost
 *                                their Engage admin role mid-session
 * Every other 403 (ENGAGE_ROLE_REQUIRED, NOT_ALLOWED_WHILE_IMPERSONATING, …)
 * is an action refusal the caller surfaces — it must not log anyone out.
 */
export const SESSION_ENDING_403_CODES: ReadonlySet<string> = new Set([
  'ENGAGE_ACCESS_REVOKED',
  'MANAGE_AS_ACTOR_NOT_ADMIN',
  // The organisation's plan no longer includes Engage (JT1-1914)
  'ENGAGE_NOT_ENABLED_FOR_ORG',
]);

/** The JobTetris billing page, where marketing-contact packs are bought (JT1-1867). */
export function jtBillingUrl(): string {
  // JobTetris's routes sit at the root of its host: /app is only where its built files live.
  return `${JT_APP_URL.replace(/\/+$/, '')}/dashboard/admin/billing`;
}

interface ApiErrorLike {
  response?: { status?: number; data?: { error?: string; message?: string; code?: string } };
  message?: string;
}

export function getApiErrorStatus(err: unknown): number | undefined {
  return (err as ApiErrorLike | null)?.response?.status;
}

export function getApiErrorCode(err: unknown): string | undefined {
  const code = (err as ApiErrorLike | null)?.response?.data?.code;
  return typeof code === 'string' && code ? code : undefined;
}

/** The server's `{ error }` message when there is one, else a fallback. */
export function getApiErrorMessage(err: unknown, fallback = 'Something went wrong. Try again in a moment.'): string {
  const e = err as ApiErrorLike | null;
  return e?.response?.data?.error ?? e?.response?.data?.message ?? e?.message ?? fallback;
}

export function noAccessPath(reason: string): string {
  return `/auth/no-access?reason=${encodeURIComponent(reason)}`;
}

export const apiClient = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = getApiErrorStatus(error);
    const code = getApiErrorCode(error);

    if (status === 401) {
      useAuthStore.getState().clearAuth();
      // Redirect back to JT for re-auth. In dev, stay on the Engage origin
      // and route to /dev-login so the developer doesn't get punted to
      // production (or to JT, which they may not be running locally).
      window.location.href = import.meta.env.DEV ? '/dev-login' : JT_APP_URL;
    } else if (status === 403 && code && SESSION_ENDING_403_CODES.has(code)) {
      if (!window.location.pathname.startsWith('/auth/no-access')) {
        // Wipe the persisted session now, then hard-navigate. NoAccessPage
        // clears the in-memory auth state on mount. Calling clearAuth() here
        // would re-render AuthGuard first, which bounces through
        // /auth/callback ("No token provided") before the navigation lands.
        useAuthStore.persist.clearStorage();
        window.location.href = noAccessPath(code);
      }
    }
    return Promise.reject(error);
  }
);
